import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "chart.js/auto";
import { getChartTheme } from "../utils/chartTheme";
import {
  buildHoverChartOptions,
  buildQuarterLabels,
} from "../utils/portfolioChart";
import { getPortfolio } from "../apis/Dashboard";
import PortfolioSidebar from "./components/PortfolioSidebar";
import PortfolioBodyHeader from "./components/PortfolioBodyHeader";
import PerfectScrollbar from "react-perfect-scrollbar";
import StickyBox from "react-sticky-box";
import IntroducePortfolio from "./components/IntroducePortfolio";
import PortfolioListSection from "./components/PortfolioListSection";
import PortfolioMobileUI from "./components/PortfolioMobileUI";
import { buildPortfolioGuruPath } from "../utils/guruRoute";

const LIST_PAGE_SIZE = 10;

// 차트 y축 값이 너무 길어지지 않도록 단위(K/M/B)를 자동 선택한다.
function getScaleUnit(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return { scale: 1, unit: "" };
  }
  const maxValue = Math.max(...values);
  if (maxValue >= 1_000_000_000) return { scale: 1_000_000_000, unit: "B" };
  if (maxValue >= 1_000_000) return { scale: 1_000_000, unit: "M" };
  if (maxValue >= 1_000) return { scale: 1_000, unit: "K" };
  return { scale: 1, unit: "" };
}

function toQuarterLabel(value) {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  if (!normalized) return null;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const year = Number(yearMonthMatch[1]);
    const month = Number(yearMonthMatch[2]);
    if (Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12) {
      const quarter = Math.floor((month - 1) / 3) + 1;
      return `${year}-${String(quarter * 3).padStart(2, "0")}`;
    }
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  const year = parsed.getFullYear();
  const quarter = Math.floor(parsed.getMonth() / 3) + 1;
  return `${year}-${String(quarter * 3).padStart(2, "0")}`;
}

// 현재 날짜 기준으로 "완료된 마지막 분기 말월"을 계산한다.
function getLastCompletedQuarterDate(now = new Date()) {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const currentQuarter = Math.ceil(month / 3);
  const quarterEndMonth = currentQuarter * 3;
  const quarterEndDay = new Date(year, quarterEndMonth, 0).getDate();
  const todayValue = new Date(year, month - 1, now.getDate()).getTime();
  const quarterEndValue = new Date(year, quarterEndMonth - 1, quarterEndDay).getTime();

  let targetYear = year;
  let targetQuarter = todayValue >= quarterEndValue ? currentQuarter : currentQuarter - 1;
  if (targetQuarter <= 0) {
    targetQuarter = 4;
    targetYear -= 1;
  }

  return new Date(targetYear, (targetQuarter * 3) - 1, 1);
}

function getQuarterLabelsFromPeriod(period, years = 3) {
  const quarterLabel = toQuarterLabel(period);
  if (!quarterLabel) {
    return buildQuarterLabels(getLastCompletedQuarterDate(new Date()), years);
  }

  const [yearStr, monthStr] = quarterLabel.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return buildQuarterLabels(getLastCompletedQuarterDate(new Date()), years);
  }

  return buildQuarterLabels(new Date(year, month - 1, 1), years);
}

// 라우트 카테고리별 기본 UI 동작을 정의한다.
const PORTFOLIO_PAGE_CONFIGS = {
  guru: {
    // 기본 동작과 동일하지만 route 해석 구조를 맞추기 위해 엔트리를 유지한다.
  },
  fund: {
    // 기본 동작과 동일하지만 route 해석 구조를 맞추기 위해 엔트리를 유지한다.
  },
  algo: {
    showInvestChart: false,
    showPercentChart: true,
  },
};

export default function PortfolioPageBase({
  showInvestChart,
  showPercentChart,
}) {
  const { category, guruName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [msgShow, setMsgShow] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [tabActive, setTabActive] = useState("introduce");
  const [listPage, setListPage] = useState(1);
  const [hoverTicker, setHoverTicker] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [portfolioData, setPortfolioData] = useState(null);
  const [portfolioError, setPortfolioError] = useState("");

  // prop > routeConfig > 기본값 순으로 화면 옵션을 해석한다.
  const routeConfig = category ? PORTFOLIO_PAGE_CONFIGS[category] : null;
  const resolvedAllTickerKey = "allTicker";
  const resolvedShowInvestChart = showInvestChart ?? routeConfig?.showInvestChart ?? true;
  const resolvedShowPercentChart = showPercentChart ?? routeConfig?.showPercentChart ?? false;

  // selectedDashboard가 바뀌면 guruId 기준으로 상세 데이터/탭 상태가 연동된다.
  const guruId = selectedDashboard?.guruid || null;
  const isAllView = tabActive === "all";
  const quarterLabels = useMemo(() => getQuarterLabelsFromPeriod(portfolioData?.period, 3), [portfolioData?.period]);

  // 모바일 변경감지
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 991.98px)");
    const handleMobileChange = () => setIsMobile(mediaQuery.matches);
    handleMobileChange();
    mediaQuery.addEventListener("change", handleMobileChange);
    return () => mediaQuery.removeEventListener("change", handleMobileChange);
  }, []);

  useEffect(() => {
    const checkDarkMode = () => {
      const skin = document.documentElement.getAttribute("data-skin");
      setIsDarkMode(skin === "dark");
    };

    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-skin"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isMobile) {
      setMsgShow(false);
    }
  }, [isMobile]);

  useEffect(() => {
    setTabActive("introduce");
  }, [guruId]);

  useEffect(() => {
    setListPage(1);
  }, [tabActive, guruId]);

  // 좌측에서 투자자를 선택하면 해당 guruid의 포트폴리오 상세를 조회한다.
  useEffect(() => {
    if (!guruId) {
      setPortfolioData(null);
      setPortfolioError("");
      setPortfolioLoading(false);
      return;
    }

    let cancelled = false;
    setPortfolioLoading(true);
    setPortfolioError("");

    getPortfolio(guruId)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch portfolio data");
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setPortfolioData(data);
      })
      .catch((error) => {
        if (cancelled) return;
        setPortfolioData(null);
        setPortfolioError("포트폴리오 데이터를 불러오지 못했습니다.");
      })
      .finally(() => {
        if (!cancelled) {
          setPortfolioLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [guruId]);

  // 탭별 목록 키를 API 응답 구조(top10/newBuy/buyMore/sold/allTicker)에 매핑한다.
  const resolveListByTab = useCallback((activeKey) => {
    if (!portfolioData) return [];
    if (activeKey === "newbuy") return portfolioData.newBuy || [];
    if (activeKey === "addbuy") return portfolioData.buyMore || [];
    if (activeKey === "sell") return portfolioData.sold || [];
    if (activeKey === "all") {
      return (
        portfolioData[resolvedAllTickerKey] ||
        portfolioData.allTicker ||
        portfolioData.allTickers ||
        [
          ...(portfolioData.top10 || []),
          ...(portfolioData.newBuy || []),
          ...(portfolioData.buyMore || []),
          ...(portfolioData.sold || []),
        ]
      );
    }
    return portfolioData.top10 || [];
  }, [portfolioData, resolvedAllTickerKey]);

  // 현재 탭 기준 목록/페이지 계산값을 메모이제이션한다.
  const activeList = useMemo(() => {
    return resolveListByTab(tabActive);
  }, [resolveListByTab, tabActive]);

  const allList = useMemo(() => {
    return resolveListByTab("all");
  }, [resolveListByTab]);

  const totalListPages = useMemo(() => {
    return Math.max(1, Math.ceil(activeList.length / LIST_PAGE_SIZE));
  }, [activeList.length]);

  const currentListPage = useMemo(() => {
    return Math.min(listPage, totalListPages);
  }, [listPage, totalListPages]);

  const paginatedList = useMemo(() => {
    const startIndex = (currentListPage - 1) * LIST_PAGE_SIZE;
    return activeList.slice(startIndex, startIndex + LIST_PAGE_SIZE);
  }, [activeList, currentListPage]);

  // 차트 hover 기준 종목은 현재 페이지 목록 안에서 우선 유지하고, 없으면 첫 항목으로 보정한다.
  useEffect(() => {
    if (isAllView) {
      setHoverTicker(null);
      return;
    }
    if (hoverTicker && paginatedList.some((item) => item.ticker === hoverTicker)) {
      return;
    }
    setHoverTicker(paginatedList[0]?.ticker || activeList[0]?.ticker || null);
  }, [paginatedList, activeList, hoverTicker, isAllView]);

  const displayTicker = hoverTicker || paginatedList[0]?.ticker || activeList[0]?.ticker || null;
  const displayItem = useMemo(() => {
    if (!displayTicker) return null;
    return activeList.find((item) => item.ticker === displayTicker) || null;
  }, [activeList, displayTicker]);
  const displayName = displayItem?.name || displayItem?.ticker || displayTicker;

  // chartData[*].period/date 값을 분기 라벨로 맞춰 티커별 shares 맵을 만든다.
  const chartSeriesContext = useMemo(() => {
    const chartDataEntries = portfolioData?.chartData || {};
    const tickerSeriesMap = {};

    Object.entries(chartDataEntries).forEach(([ticker, points]) => {
      const dateToShares = new Map();

      if (Array.isArray(points)) {
        points.forEach((point) => {
          const date = toQuarterLabel(point?.period ?? point?.date);
          const shares = Number(point?.shares);
          if (!date || !Number.isFinite(shares)) return;
          dateToShares.set(date, shares);
        });
      }

      tickerSeriesMap[ticker] = { dateToShares };
    });

    return {
      tickerSeriesMap,
    };
  }, [portfolioData?.chartData]);

  // 종목 hover 시 조회되는 차트 데이터
  const hoverChartData = useMemo(() => {
    if (!displayTicker) return null;

    const selectedSeries = chartSeriesContext.tickerSeriesMap[displayTicker];
    if (!selectedSeries || selectedSeries.dateToShares.size === 0) return null;

    const theme = getChartTheme(isDarkMode);

    if (resolvedShowPercentChart) {
      // 완료된 분기 라벨 기준 전체 합계를 만든 뒤, 표시 종목의 점유율(%)로 변환한다.
      const labels = quarterLabels;
      if (labels.length === 0) return null;

      const totals = labels.map((label) => {
        return Object.values(chartSeriesContext.tickerSeriesMap).reduce((sum, series) => {
          return sum + (series.dateToShares.get(label) || 0);
        }, 0);
      });
      const values = labels.map((label) => selectedSeries.dateToShares.get(label) || 0);
      const percentValues = values.map((value, index) => {
        const total = totals[index] || 0;
        if (!total) return 0;
        return Math.round((value / total) * 1000) / 10;
      });

      return {
        labels,
        datasets: [
          {
            label: `${displayName} 보유 비중`,
            data: percentValues,
            borderColor: theme.dataLine,
            backgroundColor: theme.dataFill,
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 2,
            pointHoverRadius: 4,
            fill: true,
          },
        ],
        _unit: "%",
      };
    }

    const labels = quarterLabels;
    const values = labels.map((label) => selectedSeries.dateToShares.get(label) || 0);
    const { scale, unit } = getScaleUnit(values);
    
    return { 
      labels,
      datasets: [
        {
          label: `${displayName} 보유주`,
          data: values.map((value) => value / scale),
          borderColor: theme.dataLine,
          backgroundColor: theme.dataFill,
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: true,
        },
      ],
      _unit: unit,
    };
  }, [displayTicker, chartSeriesContext, displayName, isDarkMode, resolvedShowPercentChart, quarterLabels]);

  // 종목 hover 시 조회되는 차트 옵션
  const hoverChartOptions = useMemo(() => {
    const dateLabelFormatter = (_, index) => hoverChartData?.labels?.[index] || "";
    const percentFormatter = (value) => Number(value).toFixed(1);
    const yFormatter = resolvedShowPercentChart ? percentFormatter : null;
    return buildHoverChartOptions(isDarkMode, hoverChartData?._unit || "", dateLabelFormatter, yFormatter);
  }, [isDarkMode, hoverChartData, resolvedShowPercentChart]);

  const onNavigateTicker = useCallback((ticker) => {
    navigate(`/stock/${ticker}`);
  }, [navigate]);

  // 사이드바 선택 시 화면 상태를 갱신하고 필요하면 URL도 동기화한다.
  const handleSelectDashboard = useCallback((dashboard, options = {}) => {
    setSelectedDashboard(dashboard);
    const targetPath = buildPortfolioGuruPath(category || "guru", dashboard);
    const shouldSyncPath = !options.fromAuto || Boolean(guruName);
    if (shouldSyncPath && location.pathname !== targetPath) {
      navigate(targetPath, { replace: Boolean(options.fromAuto) });
    }
    if (options.fromAuto) {
      setMsgShow((prev) => (isMobile ? prev : true));
      return;
    }
    setMsgShow(true);
  }, [category, guruName, isMobile, location.pathname, navigate]);

  const handleSelectTab = useCallback((nextKey) => {
    setTabActive(nextKey);
  }, []);

  // Introduce 탭에서 사용하는 데이터/핸들러를 하나의 모델로 전달한다.
  const introduceModel = useMemo(() => {
    return {
      portfolioData,
      portfolioLoading,
      portfolioError,
      guruAvatar: selectedDashboard?.guruid ? `/images/webp/${selectedDashboard.guruid}.webp` : null,
      allTickers: allList,
      showInvestChart: resolvedShowInvestChart,
      onNavigateTicker,
    };
  }, [
    portfolioData,
    portfolioLoading,
    portfolioError,
    selectedDashboard,
    allList,
    resolvedShowInvestChart,
    onNavigateTicker,
  ]);

  // 각 탭이 공통으로 쓰는 목록/페이징/hover/차트 데이터를 모델 형태로 구성한다.
  const buildListModel = useCallback((activeKey, isActiveTab = false) => {
    const tabList = isActiveTab ? activeList : resolveListByTab(activeKey);
    const tabTotalPages = isActiveTab ? totalListPages : Math.max(1, Math.ceil(tabList.length / LIST_PAGE_SIZE));
    const tabCurrentPage = isActiveTab ? currentListPage : 1;
    const tabPaginatedList = isActiveTab
      ? paginatedList
      : tabList.slice(0, LIST_PAGE_SIZE);
    const fallbackDisplayName = tabPaginatedList[0]?.name || tabPaginatedList[0]?.ticker || "";

    return {
      activeKey,
      portfolioLoading,
      portfolioError,
      activeList: tabList,
      paginatedList: tabPaginatedList,
      pagination: {
        currentPage: tabCurrentPage,
        totalPages: tabTotalPages,
        onPrev: isActiveTab
          ? () => setListPage((prev) => Math.max(1, prev - 1))
          : () => { },
        onNext: isActiveTab
          ? () => setListPage((prev) => Math.min(tabTotalPages, prev + 1))
          : () => { },
      },
      onNavigateTicker,
      onHoverEnter: isActiveTab ? setHoverTicker : () => { },
      onHoverLeave: isActiveTab ? () => setHoverTicker(null) : () => { },
      hoverChartData: isActiveTab ? hoverChartData : null,
      hoverChartOptions: isActiveTab ? hoverChartOptions : null,
      displayName: isActiveTab ? displayName : fallbackDisplayName,
      showPercentChart: resolvedShowPercentChart,
      isMobile,
    };
  }, [
    activeList,
    totalListPages,
    currentListPage,
    paginatedList,
    resolveListByTab,
    portfolioLoading,
    portfolioError,
    onNavigateTicker,
    hoverChartData,
    hoverChartOptions,
    displayName,
    resolvedShowPercentChart,
    isMobile,
  ]);

  const listModel = useMemo(() => {
    return buildListModel(tabActive, true);
  }, [buildListModel, tabActive]);

  return (
    <React.Fragment>
      <div className="h-100">
        <div className={"chat-panel portfolio-chat-panel" + (msgShow ? " msg-show" : "")} style={{ minHeight: "100vh" }}>
          <PortfolioSidebar
            category={category}
            onSelectDashboard={handleSelectDashboard}
            requestedGuruName={guruName || ""}
          />
          {isMobile ? (
            // 모바일은 Header+Content를 하나의 스와이프 UI로 렌더링한다.
            <PortfolioMobileUI
              dashboard={selectedDashboard}
              category={category}
              tabActive={tabActive}
              onSelectTab={handleSelectTab}
              onBack={() => setMsgShow(false)}
              introduceModel={introduceModel}
              buildListModel={buildListModel}
            />
          ) : (
            // 데스크톱은 Sticky 헤더 + 스크롤 본문(소개/리스트)을 분리한다.
            <div className="chat-body">
              <StickyBox offsetTop={72} className="portfolio-body-sticky">
                <PortfolioBodyHeader
                  dashboard={selectedDashboard}
                  category={category}
                  tabActive={tabActive}
                  onSelectTab={handleSelectTab}
                  onBack={() => setMsgShow(false)}
                />
              </StickyBox>
              <PerfectScrollbar className="chat-body-content">
                {tabActive === "introduce" ? (
                  <IntroducePortfolio model={introduceModel} />
                ) : (
                  <PortfolioListSection model={listModel} />
                )}
              </PerfectScrollbar>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}
